import { Modal } from "../../../../../shared/ui/Modal";
import { useEffect, useMemo, useState } from "react";
import styles from "./MoveModal.module.scss";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { CancelButton } from "@/frontend/shared/ui/Button/CancelButton/CancelButton";
import { ELOCAL_STORAGE_KEY } from "@/frontend/shared/const/const";
import { TPipeline } from "@/libs/models/pipelines";
import { useQuery } from "@tanstack/react-query";
import { getPipelines } from "@/frontend/pages/DashboardPage/model/services/getPipelines";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { useStageNames } from "../../../model/getStageNames";
import { Typography } from "@/frontend/shared/ui/Typography";
import { SelectionDropdown } from "@/frontend/shared/ui/SelectionDropdown";
import { CaretDownIcon } from "@/frontend/shared/assets/icons";
import clsx from "clsx";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { moveCompany } from "../../../model/moveCompany";
import { AxiosError } from "axios";

interface MoveModalProps {
  isOpen: boolean;
  id: number;
  onClose: () => void;
  triggerRefetch: () => void;
  initialPipeline?: string;
}

export function MoveModal({ isOpen, onClose, id, triggerRefetch, initialPipeline = "" }: MoveModalProps) {
  const [isStageDropdownSelectorOpen, setIsStageDropdownSelectorOpen] = useState(false);
  const [isPipelineDropdownSelectorOpen, setIsPipelineDropdownSelectorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [pipelineSelected, setPipelineSelected] = useState<string>(initialPipeline);

  useEffect(() => {
    if (initialPipeline) setPipelineSelected(initialPipeline);
  }, [initialPipeline]);

  const {
    data: pipelinesData,
    isLoading: isLoadingPipelines,
    refetch: refetchPipelines
  } = useQuery({
    queryKey: ["pipelines"],
    queryFn: async () => {
      const res = await getPipelines();

      return res;
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
    throwOnError: (error) => {
      console.error(error);
      reportErrorToSentry(error, "DISCOVER: /pipelines");
      snackbarConstructor("Error fetching pipelines", "error");
      return true;
    }
  });

  useEffect(() => {
    if (!!pipelinesData && pipelinesData.length > 0 && !pipelineSelected) {
      setPipelineSelected(pipelinesData[0].name);
    }
  }, [pipelinesData, pipelineSelected]);

  const { data: stages } = useStageNames(pipelinesData?.find((pipeline) => pipeline.name === pipelineSelected)?.id);
  const pipelineOptions = pipelinesData?.map((s) => ({ value: s.id.toString(), label: s.name })) ?? [];

  const [stageSelected, setStageSelected] = useState<string>("");

  const stageOptions = useMemo(() => stages?.map((s) => ({ value: s.id.toString(), label: s.name })) ?? [], [stages]);
  useEffect(() => {
    const initialSelectedStage = stageOptions?.[0]?.label;
    setStageSelected(initialSelectedStage);
  }, [stages, stageOptions]);

  function onCloseModalHandler() {
    onClose();
  }

  async function handleMove() {
    try {
      setIsLoading(true);
      const stageId = stages?.find((s) => s.name === stageSelected)?.id;

      if (!stageId) {
        snackbarConstructor("Please select a stage", "error");
        return;
      }

      await moveCompany({ id, stageId });
      snackbarConstructor("Company was moved successfully", "normal");
      triggerRefetch();
      onCloseModalHandler();
    } catch (e) {
      const axiosErr = e as AxiosError;
      if (axiosErr.response?.status === 400) {
        snackbarConstructor("Company with this website already exists in the pipeline", "error");
      } else {
        snackbarConstructor("Error while moving company", "error");
        reportErrorToSentry(axiosErr, "MOVE COMPANY: /portfolio-companies/${id}/stage");
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div>
      <Modal isOpen={isOpen} onClose={onCloseModalHandler} title="Move" isHeader>
        <div>
          <div className={styles.selectionContainer}>
            <Typography variant="label-4" color="greyscale700">
              Pipeline
            </Typography>
            <SelectionDropdown
              isMaxWidth
              className={styles.selectionDropdown}
              value={pipelineSelected}
              isOpen={isPipelineDropdownSelectorOpen}
              onFocus={() => setIsPipelineDropdownSelectorOpen((prev) => !prev)}
              onChange={(pipelineId) => {
                const _pipelineName = pipelinesData?.find((s) => s?.id?.toString() === pipelineId?.toString());
                setPipelineSelected(_pipelineName?.name ?? "");
                setIsPipelineDropdownSelectorOpen(false);
              }}
              onClose={() => setIsPipelineDropdownSelectorOpen(false)}
              options={pipelineOptions}
            >
              <div className={styles.entryPointsContainer}>
                <Typography variant="label-4" className={styles.entryPoints} color="greyscale900">
                  {pipelineSelected}
                </Typography>
                <CaretDownIcon
                  className={clsx(styles.caretDownIcon, isPipelineDropdownSelectorOpen && styles.rotateIcon)}
                />
              </div>
            </SelectionDropdown>
          </div>
          <div className={styles.selectionContainer}>
            <Typography variant="label-4" color="greyscale700">
              Stage
            </Typography>
            {!!stageSelected ? (
              <SelectionDropdown
                isMaxWidth
                className={styles.selectionDropdown}
                value={stageSelected}
                isOpen={isStageDropdownSelectorOpen}
                onFocus={() => setIsStageDropdownSelectorOpen((prev) => !prev)}
                onChange={(stageId) => {
                  const _stageName = stages?.find((s) => s?.id?.toString() === stageId?.toString());
                  setStageSelected(_stageName?.name ?? "");
                  setIsStageDropdownSelectorOpen(false);
                }}
                onClose={() => setIsStageDropdownSelectorOpen(false)}
                options={stageOptions}
              >
                <div className={styles.entryPointsContainer}>
                  <Typography variant="label-4" className={styles.entryPoints} color="greyscale900">
                    {stageSelected}
                  </Typography>

                  <CaretDownIcon
                    className={clsx(styles.caretDownIcon, isStageDropdownSelectorOpen && styles.rotateIcon)}
                  />
                </div>
              </SelectionDropdown>
            ) : (
              <div className={styles.loaderContainer}>
                <Loader />
              </div>
            )}
          </div>
        </div>
        <div className={styles.optionContainer}>
          <CancelButton size="large" onClick={onCloseModalHandler}>
            Cancel
          </CancelButton>
          <NormalButton size="large" onClick={handleMove} isDisabled={isLoading}>
            Move
          </NormalButton>
        </div>
      </Modal>
    </div>
  );
}
